import { apiGet, apiPost, apiPut } from '../../lib/api'
import { bookQuery, type BookAsk } from '../../lib/bookAsk'
import { withAuth } from '../auth/token'
import type { Address, ComplianceState } from '../brokers/api'
import type { FmcsaVerification } from '../fmcsa/api'

/**
 * Mirrors the carrier and fleet slices on the API. Address and compliance shapes are
 * the broker's — same wire format, one definition.
 */
export type EquipmentType = 'DryVan' | 'Reefer' | 'Flatbed' | 'StepDeck' | 'DoubleDrop' | 'StraightBoxTruck'

/** Every type, in the order the selects offer them — a load can ride on any of them (TRKC-100). */
export const EQUIPMENT_TYPES: readonly EquipmentType[] = [
  'DryVan',
  'Reefer',
  'Flatbed',
  'StepDeck',
  'DoubleDrop',
  'StraightBoxTruck',
]

/** A straight box truck hauls without a trailer: it is never a trailer's type (TRKC-100). */
export const TRAILER_TYPES: readonly EquipmentType[] = EQUIPMENT_TYPES.filter((type) => type !== 'StraightBoxTruck')

/**
 * What a truck is (TRKC-102, Hugo 2026-10-12): a tractor by its cab, or a straight box truck.
 * A van, a reefer or a flatbed is a trailer's type, never a truck's.
 */
export type TruckType = 'SleeperCab' | 'DayCab' | 'StraightBoxTruck'

/** Every type a truck can be, in the order the select offers them — Hugo's fleets run mostly sleeper cabs. */
export const TRUCK_TYPES: readonly TruckType[] = ['SleeperCab', 'DayCab', 'StraightBoxTruck']

/**
 * The one truck that carries its own load, states its own room, features and box, and is
 * posted alone — the same name as a truck's type and as the equipment it offers.
 */
export const STRAIGHT_BOX_TRUCK = 'StraightBoxTruck' as const

/** The lengths a straight box truck's box comes in (Hugo, TRKC-102) — a MIRROR of the server's `BoxTruckLengths`. */
export const BOX_LENGTHS: readonly number[] = [14, 16, 24, 26]

/** What a unit has besides its type, in DAT's words (TRKC-100): VA, VG, VV, VS/FN/SN. */
export type EquipmentFeature = 'AirRide' | 'LiftGate' | 'Vented' | 'Conestoga'

export const EQUIPMENT_FEATURES: readonly EquipmentFeature[] = ['AirRide', 'LiftGate', 'Vented', 'Conestoga']

/**
 * Which features each type can have — a MIRROR of the server's `EquipmentFeatureRules.AllowedOn`,
 * used only to decide which boxes a form offers. The server judges every save.
 */
const FEATURES_ALLOWED: Readonly<Record<EquipmentType, readonly EquipmentFeature[]>> = {
  DryVan: ['AirRide', 'LiftGate', 'Vented', 'Conestoga'],
  Reefer: ['AirRide', 'LiftGate'],
  Flatbed: ['AirRide', 'Conestoga'],
  StepDeck: ['AirRide', 'Conestoga'],
  DoubleDrop: ['AirRide'],
  StraightBoxTruck: ['AirRide', 'LiftGate'],
}

/**
 * The features any of these types can have, in their declared order. A form holds `''`
 * before a type is chosen, and a type this build does not know allows nothing.
 */
export function featuresAllowedOn(...types: (EquipmentType | '' | null | undefined)[]): EquipmentFeature[] {
  const allowedFor = (type: EquipmentType | '' | null | undefined): readonly EquipmentFeature[] =>
    type ? (FEATURES_ALLOWED[type] ?? []) : []

  return EQUIPMENT_FEATURES.filter((feature) => types.some((type) => allowedFor(type).includes(feature)))
}

/**
 * An open deck ties its freight down, so securement gear belongs to it — a MIRROR of the
 * server's `EquipmentKinds.IsOpenDeck` (TRKC-100).
 */
export function isOpenDeck(type: EquipmentType | '' | null | undefined): boolean {
  return type === 'Flatbed' || type === 'StepDeck' || type === 'DoubleDrop'
}

export interface CarrierSummary {
  id: string
  name: string
  mcNumber: string | null
  commissionPercentage: number
  city: string
  state: string
  complianceState: ComplianceState
  active: boolean
}

export interface DriverView {
  id: string
  firstName: string
  lastName: string
  phone: string | null
  email: string | null
  cdlNumber: string | null
  /** The state that issued the CDL; null with no number, or on a driver saved before it was asked (TRKC-97). */
  cdlState: string | null
  complianceState: ComplianceState
  /** This one's own standing, or its carrier's when the carrier's is worse. */
  effectiveComplianceState: ComplianceState
  /**
   * True when the carrier is what holds it back, not its own paperwork — a carrier that
   * is only Pending as much as a Blocked one. It names who, never how hard: that is the
   * carrier's own state.
   */
  blockedByCarrier: boolean
  active: boolean
  deactivatedAt: string | null
  /**
   * Composed by the server from four facts — its papers, the carrier's papers, its
   * activation and the carrier's. Never recomputed here: a screen that got the rule
   * slightly wrong would offer somebody it should not.
   */
  availableForDispatch: boolean
  /** The version an edit is made on — only a save of this record's form moves it. */
  updatedAt: string
}

export interface TruckView {
  id: string
  unitNumber: string
  /**
   * Null on a truck saved before trucks were typed by cab (TRKC-102) that is not a box truck:
   * not stated until its next save asks for it.
   */
  type: TruckType | null
  make: string | null
  model: string | null
  vin: string | null
  licensePlate: string | null
  /** A straight box truck's own room; null on any other truck, whose trailer states it (TRKC-100). */
  palletCapacity: number | null
  weightCapacityPounds: number | null
  /** A straight box truck's features; none on any other truck. */
  features: EquipmentFeature[]
  /** A straight box truck's box in feet; null on any other truck, or not stated yet (TRKC-102). */
  boxLengthFeet: number | null
  complianceState: ComplianceState
  /** This one's own standing, or its carrier's when the carrier's is worse. */
  effectiveComplianceState: ComplianceState
  /**
   * True when the carrier is what holds it back, not its own paperwork — a carrier that
   * is only Pending as much as a Blocked one. It names who, never how hard: that is the
   * carrier's own state.
   */
  blockedByCarrier: boolean
  active: boolean
  deactivatedAt: string | null
  /**
   * Composed by the server from four facts — its papers, the carrier's papers, its
   * activation and the carrier's. Never recomputed here: a screen that got the rule
   * slightly wrong would offer somebody it should not.
   */
  availableForDispatch: boolean
  /** The version an edit is made on — only a save of this record's form moves it. */
  updatedAt: string
}

export interface TrailerView {
  id: string
  trailerNumber: string
  type: EquipmentType
  vin: string | null
  palletCapacity: number | null
  weightCapacityPounds: number | null
  /** What the trailer states it has besides its type (TRKC-100); a load asking for one matches only if it is here. */
  features: EquipmentFeature[]
  complianceState: ComplianceState
  /** This one's own standing, or its carrier's when the carrier's is worse. */
  effectiveComplianceState: ComplianceState
  /**
   * True when the carrier is what holds it back, not its own paperwork — a carrier that
   * is only Pending as much as a Blocked one. It names who, never how hard: that is the
   * carrier's own state.
   */
  blockedByCarrier: boolean
  active: boolean
  deactivatedAt: string | null
  /**
   * Composed by the server from four facts — its papers, the carrier's papers, its
   * activation and the carrier's. Never recomputed here: a screen that got the rule
   * slightly wrong would offer somebody it should not.
   */
  availableForDispatch: boolean
  /** The version an edit is made on — only a save of this record's form moves it. */
  updatedAt: string
}

/**
 * What stands behind a carrier's compliance state, by kind — document types as the API
 * names them, all four empty when its papers are in order. Read by the server at the
 * moment of asking, with the evaluation's own rules (TRKC-89).
 */
export interface ComplianceReasons {
  missing: string[]
  expired: string[]
  belowMinimum: string[]
  notYetEffective: string[]
}

export interface CarrierDetail {
  id: string
  name: string
  mcNumber: string | null
  usdotNumber: string | null
  taxId: string | null
  commissionPercentage: number
  address: Address
  phone: string | null
  email: string | null
  complianceState: ComplianceState
  /**
   * Null when today's reading and the state on file disagree — the evaluation has not
   * caught up with the calendar yet — and there is nothing true to add to the state.
   */
  complianceReasons: ComplianceReasons | null
  active: boolean
  deactivatedAt: string | null
  drivers: DriverView[]
  trucks: TruckView[]
  trailers: TrailerView[]
  /** What the federal register said last time, or null if nobody has asked. */
  fmcsaVerification: FmcsaVerification | null
  createdAt: string
  updatedAt: string
}

export interface CarrierPayload {
  name: string
  mcNumber: string | null
  usdotNumber: string | null
  taxId: string | null
  commissionPercentage: number
  address: {
    line1: string
    line2: string | null
    city: string
    state: string
    postalCode: string
  }
  phone: string | null
  email: string | null
  /**
   * The `updatedAt` the form was opened on. Required on an edit: a save made on an
   * older reading is refused, so a form left open cannot put back what somebody else
   * saved since. Only a save of this form moves it — not the compliance evaluation,
   * not a paper landing, not an activation.
   */
  version?: string
}

export interface DriverPayload {
  firstName: string
  lastName: string
  phone: string | null
  email: string | null
  cdlNumber: string | null
  /** Required beside a number: a state prints its own numbers, so the pair is the license (TRKC-97). */
  cdlState: string | null
  /**
   * The `updatedAt` the form was opened on. Required on an edit: a save made on an
   * older reading is refused, so a form left open cannot put back what somebody else
   * saved since. Only a save of this form moves it — not the compliance evaluation,
   * not a paper landing, not an activation.
   */
  version?: string
}

export interface TruckPayload {
  unitNumber: string
  type: string
  make: string | null
  model: string | null
  vin: string | null
  licensePlate: string | null
  /** A straight box truck's own room and features (TRKC-100); refused on any other truck. */
  palletCapacity: number | null
  weightCapacityPounds: number | null
  features: EquipmentFeature[]
  /** A straight box truck's box — 14, 16, 24 or 26 ft (TRKC-102); refused on any other truck. */
  boxLengthFeet: number | null
  /**
   * The `updatedAt` the form was opened on. Required on an edit: a save made on an
   * older reading is refused, so a form left open cannot put back what somebody else
   * saved since. Only a save of this form moves it — not the compliance evaluation,
   * not a paper landing, not an activation.
   */
  version?: string
}

export interface TrailerPayload {
  trailerNumber: string
  type: string
  vin: string | null
  palletCapacity: number | null
  weightCapacityPounds: number | null
  features: EquipmentFeature[]
  /**
   * The `updatedAt` the form was opened on. Required on an edit: a save made on an
   * older reading is refused, so a form left open cannot put back what somebody else
   * saved since. Only a save of this form moves it — not the compliance evaluation,
   * not a paper landing, not an activation.
   */
  version?: string
}

export function listCarriers(ask: BookAsk = {}): Promise<CarrierSummary[]> {
  return apiGet<CarrierSummary[]>(`/api/carriers${bookQuery(ask)}`, { headers: withAuth() })
}

/** The book searched for a picker that may name any carrier — a filter's. Module-level, so its identity holds. */
export const searchCarriers = (term: string, limit: number) => listCarriers({ search: term, limit })

/** The book searched for a picker that posts a carrier's unit: only the carriers we work with. */
export const searchActiveCarriers = (term: string, limit: number) => listCarriers({ search: term, limit, active: true })

export function getCarrier(id: string): Promise<CarrierDetail> {
  return apiGet<CarrierDetail>(`/api/carriers/${id}`, { headers: withAuth() })
}

export function registerCarrier(payload: CarrierPayload): Promise<CarrierDetail> {
  return apiPost<CarrierDetail>('/api/carriers', payload, { headers: withAuth() })
}

export function updateCarrier(id: string, payload: CarrierPayload): Promise<CarrierDetail> {
  return apiPut<CarrierDetail>(`/api/carriers/${id}`, payload, { headers: withAuth() })
}

export function addDriver(carrierId: string, payload: DriverPayload): Promise<DriverView> {
  return apiPost<DriverView>(`/api/carriers/${carrierId}/drivers`, payload, {
    headers: withAuth(),
  })
}

export function updateDriver(
  carrierId: string,
  driverId: string,
  payload: DriverPayload,
): Promise<DriverView> {
  return apiPut<DriverView>(`/api/carriers/${carrierId}/drivers/${driverId}`, payload, {
    headers: withAuth(),
  })
}

export function addTruck(carrierId: string, payload: TruckPayload): Promise<TruckView> {
  return apiPost<TruckView>(`/api/carriers/${carrierId}/trucks`, payload, {
    headers: withAuth(),
  })
}

export function updateTruck(
  carrierId: string,
  truckId: string,
  payload: TruckPayload,
): Promise<TruckView> {
  return apiPut<TruckView>(`/api/carriers/${carrierId}/trucks/${truckId}`, payload, {
    headers: withAuth(),
  })
}

export function addTrailer(carrierId: string, payload: TrailerPayload): Promise<TrailerView> {
  return apiPost<TrailerView>(`/api/carriers/${carrierId}/trailers`, payload, {
    headers: withAuth(),
  })
}

export function updateTrailer(
  carrierId: string,
  trailerId: string,
  payload: TrailerPayload,
): Promise<TrailerView> {
  return apiPut<TrailerView>(`/api/carriers/${carrierId}/trailers/${trailerId}`, payload, {
    headers: withAuth(),
  })
}
